import { useEffect, useMemo, useRef, useState } from "react";
import {
  addDays,
  differenceInCalendarDays,
  endOfMonth,
  format,
  isBefore,
  isSameMonth,
  parseISO,
  startOfMonth,
} from "date-fns";

interface UseDateRangePickerParams {
  startDate: string;
  endDate: string;
  onChange: (startDate: string, endDate: string) => void;
  maxRangeDays: number;
  placeholder: string;
}

const toInputValue = (date: Date) => format(date, "yyyy-MM-dd");

const diffDaysInclusive = (start: Date, end: Date) =>
  differenceInCalendarDays(end, start) + 1;

export const useDateRangePicker = ({
  startDate,
  endDate,
  onChange,
  maxRangeDays,
  placeholder,
}: UseDateRangePickerParams) => {
  const [isOpen, setIsOpen] = useState(false);
  const [rangeError, setRangeError] = useState<string>("");
  const [focusedDate, setFocusedDate] = useState<Date | null>(null);
  const [tempStart, setTempStart] = useState<Date | null>(startDate ? parseISO(startDate) : null);
  const [tempEnd, setTempEnd] = useState<Date | null>(endDate ? parseISO(endDate) : null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const start = useMemo(() => (startDate ? parseISO(startDate) : null), [startDate]);
  const end = useMemo(() => (endDate ? parseISO(endDate) : null), [endDate]);

  const [activeMonth, setActiveMonth] = useState<Date>(() => startOfMonth(start ?? new Date()));

  useEffect(() => {
    if (isOpen) {
      const base = tempStart ?? start ?? new Date();
      setActiveMonth(startOfMonth(base));
      setFocusedDate(tempStart ?? start ?? new Date());
    }
  }, [isOpen, start, tempStart]);

  useEffect(() => {
    if (isOpen) {
      setTempStart(startDate ? parseISO(startDate) : null);
      setTempEnd(endDate ? parseISO(endDate) : null);
      setRangeError("");
    }
  }, [isOpen, startDate, endDate]);

  useEffect(() => {
    if (!isOpen || !focusedDate) return;
    const target = wrapperRef.current?.querySelector<HTMLButtonElement>(
      `[data-date="${toInputValue(focusedDate)}"]`
    );
    target?.focus();
  }, [focusedDate, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const displayValue = useMemo(() => {
    if (start && end) {
      return `${format(start, "dd/MM/yyyy")} - ${format(end, "dd/MM/yyyy")}`;
    }
    if (start && !end) {
      return `${format(start, "dd/MM/yyyy")} - ...`;
    }
    return placeholder;
  }, [start, end, placeholder]);

  const handleSelectDate = (date: Date, monthContext: Date) => {
    if (!isSameMonth(date, monthContext)) {
      setActiveMonth(startOfMonth(date));
    }

    if (!tempStart || (tempStart && tempEnd)) {
      setRangeError("");
      setTempStart(date);
      setTempEnd(null);
      return;
    }

    if (isBefore(date, tempStart)) {
      setRangeError("");
      setTempStart(date);
      setTempEnd(null);
      return;
    }

    const diff = diffDaysInclusive(tempStart, date);
    if (diff > maxRangeDays) {
      setRangeError(`El rango de fechas no puede exceder ${maxRangeDays} días`);
      return;
    }

    setRangeError("");
    setTempStart(tempStart);
    setTempEnd(date);
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsOpen((prev) => !prev);
    }
  };

  const handlePopoverKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
      triggerRef.current?.focus();
    }

    if (event.key === "Tab") {
      const focusable = popoverRef.current?.querySelectorAll<HTMLElement>(
        "button, [href], select, input, textarea, [tabindex]:not([tabindex='-1'])"
      );

      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  const handleDayKeyDown = (event: React.KeyboardEvent, date: Date) => {
    const key = event.key;
    if (["ArrowRight", "ArrowLeft", "ArrowUp", "ArrowDown", "Home", "End"].includes(key)) {
      event.preventDefault();
      let next = date;
      if (key === "ArrowRight") next = addDays(date, 1);
      if (key === "ArrowLeft") next = addDays(date, -1);
      if (key === "ArrowUp") next = addDays(date, -7);
      if (key === "ArrowDown") next = addDays(date, 7);
      if (key === "Home") next = startOfMonth(date);
      if (key === "End") next = endOfMonth(date);

      setFocusedDate(next);
      setActiveMonth(startOfMonth(next));
    }
  };

  const handleCancel = () => {
    setTempStart(startDate ? parseISO(startDate) : null);
    setTempEnd(endDate ? parseISO(endDate) : null);
    setRangeError("");
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const handleApply = () => {
    if (!tempStart || !tempEnd) return;
    onChange(toInputValue(tempStart), toInputValue(tempEnd));
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  return {
    activeMonth,
    displayValue,
    handleApply,
    handleCancel,
    handleDayKeyDown,
    handlePopoverKeyDown,
    handleSelectDate,
    handleTriggerKeyDown,
    isOpen,
    popoverRef,
    rangeError,
    setActiveMonth,
    setIsOpen,
    tempEnd,
    tempStart,
    toInputValue,
    triggerRef,
    wrapperRef,
  };
};
